// src/pages/analytics/filterPaneState.ts
//
// State, params and hook behind <FilterPane /> (filterPane.tsx) — split out so
// the component file only exports components (react-refresh).

import { useSyncExternalStore } from 'react';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';

export const FILTER_PANE_FEATURE = 'analytics.filterpane';

export type SalesType = 'net' | 'gross' | 'returns';
export type DateBasis = 'invoice' | 'ship';

export interface PaneState {
  salesType: SalesType;
  dateBasis: DateBasis;     // ship = InHead.Shipdate (agent 3.20.0+)
  priceLevel: string;
  area: string;
  state: string;
  city: string;
  custCategory: string;
  creditHold: '' | 'yes' | 'no';
  salesman: string;
  salesman2: string;
  driver: string;
  itemCategory: string;
  itemSubcategory: string;  // Invent.Subcat (agent 3.21.0+)
  vendor: string;
  itemType: string;
  discontinued: '' | 'yes' | 'no';
  dow: number[];            // ISO weekdays 1 (Mon) .. 7 (Sun)
}

export const EMPTY_PANE: PaneState = {
  salesType: 'net', dateBasis: 'invoice', priceLevel: '', area: '', state: '', city: '', custCategory: '', creditHold: '',
  salesman: '', salesman2: '', driver: '', itemCategory: '', itemSubcategory: '', vendor: '', itemType: '',
  discontinued: '', dow: [],
};

const STORAGE_KEY = 'analytics.filterPane.v1';

// ── Shared store (module-level; every tab subscribes) ──────────────────────
let current: PaneState = load();
const listeners = new Set<() => void>();

function load(): PaneState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...EMPTY_PANE, ...JSON.parse(raw) };
  } catch { /* private mode / blocked storage */ }
  return EMPTY_PANE;
}

function setPane(next: PaneState) {
  current = next;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  listeners.forEach(l => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/** Number of active slicers (sales type ≠ net counts as one). */
export function activeCount(s: PaneState): number {
  const { salesType, dateBasis, dow, ...rest } = s;
  return (salesType !== 'net' ? 1 : 0) + (dateBasis === 'ship' ? 1 : 0) + (dow.length > 0 ? 1 : 0)
    + Object.values(rest).filter(v => v !== '').length;
}

/** Recipe params — empty values omitted. */
export function paneParams(s: PaneState): Record<string, string | undefined> {
  const v = (x: string) => x || undefined;
  return {
    salesType:     s.salesType !== 'net' ? s.salesType : undefined,
    dateBasis:     s.dateBasis === 'ship' ? 'ship' : undefined,
    fPriceLevel:   v(s.priceLevel),
    fArea:         v(s.area),
    fState:        v(s.state),
    fCity:         v(s.city),
    fCustCategory: v(s.custCategory),
    fCreditHold:   v(s.creditHold),
    fSalesman:     v(s.salesman),
    fSalesman2:    v(s.salesman2),
    fDriver:       v(s.driver),
    fItemCategory: v(s.itemCategory),
    fItemSubcat:   v(s.itemSubcategory),
    fVendor:       v(s.vendor),
    fItemType:     v(s.itemType),
    fDiscontinued: v(s.discontinued),
    fDow:          s.dow.length ? [...s.dow].sort().join(',') : undefined,
  };
}

/**
 * The pane state for the current tenant/user. When the feature is off the
 * state is EMPTY_PANE (no params sent) and `enabled` is false so tabs can
 * skip rendering <FilterPane />.
 */
export function useFilterPane() {
  const { canSee } = useFeatureAccess();
  const enabled = canSee(FILTER_PANE_FEATURE);
  const state = useSyncExternalStore(subscribe, () => current);
  const effective = enabled ? state : EMPTY_PANE;
  return {
    enabled,
    state: effective,
    params: paneParams(effective),
    /** Stable string for React Query keys. */
    key: JSON.stringify(paneParams(effective)),
    set: (patch: Partial<PaneState>) => setPane({ ...current, ...patch }),
    /** Replace every slicer (saved views); missing keys fall back to defaults. */
    replace: (next: Partial<PaneState>) => setPane({
      ...EMPTY_PANE, ...next, dow: Array.isArray(next.dow) ? next.dow.filter(n => typeof n === 'number') : [],
    }),
    reset: () => setPane(EMPTY_PANE),
  };
}
